"use strict";
var WebhooksController_1;
var _a, _b, _c, _d, _e, _f, _g, _h;
Object.defineProperty(exports, "__esModule", { value: true });
exports.WebhooksController = void 0;
const tslib_1 = require("tslib");
const common_1 = require("@nestjs/common");
const express_1 = require("express");
const notifications_service_1 = require("../notifications/notifications.service");
const customers_service_1 = require("../customers/customers.service");
const interaction_log_entity_1 = require("../customers/interaction-log.entity");
const twilio_signature_guard_1 = require("../../common/guards/twilio-signature.guard");
const whatsapp_signature_guard_1 = require("../../common/guards/whatsapp-signature.guard");
const sendgrid_signature_guard_1 = require("../../common/guards/sendgrid-signature.guard");
/**
 * Handles all inbound webhooks from communication providers.
 * Endpoints are PUBLIC — providers POST/GET here directly.
 * Signature verification is enforced via per-route guards.
 *
 * Base path: /api/webhooks
 */
let WebhooksController = WebhooksController_1 = class WebhooksController {
    constructor(notificationsService, customersService) {
        this.notificationsService = notificationsService;
        this.customersService = customersService;
        this.logger = new common_1.Logger(WebhooksController_1.name);
    }
    // ── Twilio SMS status callback ────────────────────────────────────────────
    /**
     * POST /api/webhooks/twilio/sms
     * Configure in Twilio console: Phone Number → Status Callback URL
     *   https://your-api.vaidban.com/api/webhooks/twilio/sms
     */
    async twilioSmsStatus(payload) {
        this.logger.log(`Twilio SMS: ${payload['MessageSid']} → ${payload['MessageStatus']}`);
        await this.notificationsService.handleTwilioStatusCallback(payload);
        return { received: true };
    }
    /**
     * POST /api/webhooks/twilio/whatsapp
     * Configure in Twilio console: WhatsApp Sandbox → Status Callback URL
     *   https://your-api.vaidban.com/api/webhooks/twilio/whatsapp
     */
    async twilioWhatsAppStatus(payload) {
        this.logger.log(`Twilio WhatsApp: ${payload['MessageSid']} → ${payload['MessageStatus']}`);
        await this.notificationsService.handleTwilioStatusCallback(payload);
        return { received: true };
    }
    // ── SendGrid event webhook ────────────────────────────────────────────────
    /**
     * POST /api/webhooks/sendgrid
     * Configure in SendGrid: Settings → Mail Settings → Event Webhook
     *   https://your-api.vaidban.com/api/webhooks/sendgrid
     *
     * Set SENDGRID_WEBHOOK_PUBLIC_KEY (base64 DER key from SendGrid dashboard)
     * to enable Ed25519 signature verification. Falls back to open in stub mode.
     */
    async sendgridEvents(events) {
        this.logger.log(`SendGrid: ${Array.isArray(events) ? events.length : 0} event(s)`);
        await this.notificationsService.handleSendGridEvent(Array.isArray(events) ? events : []);
        return { received: true };
    }
    // ── Meta WhatsApp Cloud API ───────────────────────────────────────────────
    /**
     * GET /api/webhooks/whatsapp
     * Meta webhook verification challenge — called once when registering the webhook
     * in the Meta App Dashboard.
     */
    whatsappVerify(mode, token, challenge, res) {
        const verifyToken = process.env['WHATSAPP_VERIFY_TOKEN'] ?? '';
        if (mode === 'subscribe' && token === verifyToken) {
            this.logger.log('Meta WhatsApp webhook verified successfully');
            res.status(200).send(challenge); // Must return raw challenge string, not JSON
        }
        else {
            this.logger.error('Meta WhatsApp webhook verification failed');
            res.status(403).send('Forbidden');
        }
    }
    /**
     * POST /api/webhooks/whatsapp
     * Receives Meta WhatsApp event payloads:
     *  - Delivery status updates  → update notification_logs
     *  - Inbound customer messages → log in interaction_logs; auto-opt-out on STOP
     *
     * Configure in Meta App Dashboard:
     *   https://your-api.vaidban.com/api/webhooks/whatsapp
     */
    async whatsappEvents(payload) {
        const entries = payload?.['entry'] ?? [];
        for (const entry of entries) {
            const changes = entry?.['changes'] ?? [];
            for (const change of changes) {
                if (change?.['field'] !== 'messages')
                    continue;
                const value = change?.['value'] ?? {};
                const statuses = value?.['statuses'] ?? [];
                const messages = value?.['messages'] ?? [];
                // ── Delivery status updates ───────────────────────────────────────
                for (const status of statuses) {
                    const msgId = status?.['id'];
                    const state = status?.['status'];
                    if (msgId && state) {
                        await this.notificationsService.handleWhatsAppDeliveryStatus(msgId, state);
                    }
                }
                // ── Inbound customer messages ─────────────────────────────────────
                for (const msg of messages) {
                    await this.handleInboundWhatsApp(msg, value?.['contacts']?.[0]);
                }
            }
        }
        return { received: true };
    }
    // ── Opt-out / opt-in (link-based) ────────────────────────────────────────
    /**
     * GET /api/webhooks/opt-out?phone=+91XXXXXXXXXX&channel=sms
     * Linked from SMS/email footers. Opens a plain page confirming opt-out.
     */
    async optOutLink(phone, email, channel, res) {
        if (!phone && !email) {
            res.status(400).send('phone or email parameter required');
            return;
        }
        await this.notificationsService.optOut({ phone, email, channel, reason: 'Link-based opt-out' });
        res.status(200).send(`
      <html><body style="font-family:sans-serif;text-align:center;padding:60px;">
        <h2>✅ Unsubscribed</h2>
        <p>You have been successfully unsubscribed from Vaidban communications.</p>
        <p style="color:#9e9e9e;font-size:13px;">
          To opt back in, contact our support team at 1800-XXX-XXXX.
        </p>
      </body></html>
    `);
    }
    /**
     * POST /api/webhooks/opt-out — programmatic opt-out (from agent UI or other services)
     */
    async optOut(body) {
        await this.notificationsService.optOut(body);
        return { success: true };
    }
    /**
     * POST /api/webhooks/opt-in — re-subscribe a contact
     */
    async optIn(body) {
        await this.notificationsService.optIn(body);
        return { success: true };
    }
    // ── Private: inbound WhatsApp message handler ─────────────────────────────
    async handleInboundWhatsApp(msg, contact) {
        const phone = msg?.['from'];
        const msgType = msg?.['type'];
        const text = msg?.['text']?.['body'];
        const msgId = msg?.['id'];
        const senderName = contact?.['profile']?.['name'];
        if (!phone)
            return;
        const messageText = text ?? `[${msgType ?? 'non-text'} message]`;
        const isStop = /^\s*stop\s*$/i.test(text ?? '');
        this.logger.log(`WhatsApp inbound from ${phone}${senderName ? ` (${senderName})` : ''}: "${messageText.slice(0, 80)}"`);
        // Auto opt-out on STOP keyword
        if (isStop) {
            await this.notificationsService.optOut({
                phone,
                channel: 'whatsapp',
                reason: `Customer replied STOP via WhatsApp (msg: ${msgId ?? 'unknown'})`,
            });
            this.logger.log(`WhatsApp opt-out recorded for ${phone}`);
        }
        // Find the customer by phone and log the inbound message
        const customer = await this.customersService.findByPhone(phone);
        if (customer) {
            const description = isStop
                ? `[Opt-out] Customer replied STOP via WhatsApp`
                : `[WhatsApp Inbound] ${messageText}`;
            await this.customersService.addLog(customer.id, interaction_log_entity_1.LogType.WHATSAPP_INBOUND, description, undefined);
        }
        else {
            // Unknown number — log at warn level for visibility
            this.logger.warn(`WhatsApp inbound from unregistered number ${phone}: "${messageText.slice(0, 60)}"`);
        }
    }
};
exports.WebhooksController = WebhooksController;
tslib_1.__decorate([
    (0, common_1.Post)('twilio/sms'),
    (0, common_1.UseGuards)(twilio_signature_guard_1.TwilioSignatureGuard),
    tslib_1.__param(0, (0, common_1.Body)()),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [typeof (_c = typeof Record !== "undefined" && Record) === "function" ? _c : Object]),
    tslib_1.__metadata("design:returntype", Promise)
], WebhooksController.prototype, "twilioSmsStatus", null);
tslib_1.__decorate([
    (0, common_1.Post)('twilio/whatsapp'),
    (0, common_1.UseGuards)(twilio_signature_guard_1.TwilioSignatureGuard),
    tslib_1.__param(0, (0, common_1.Body)()),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [typeof (_d = typeof Record !== "undefined" && Record) === "function" ? _d : Object]),
    tslib_1.__metadata("design:returntype", Promise)
], WebhooksController.prototype, "twilioWhatsAppStatus", null);
tslib_1.__decorate([
    (0, common_1.Post)('sendgrid'),
    (0, common_1.UseGuards)(sendgrid_signature_guard_1.SendGridSignatureGuard),
    tslib_1.__param(0, (0, common_1.Body)()),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [Array]),
    tslib_1.__metadata("design:returntype", Promise)
], WebhooksController.prototype, "sendgridEvents", null);
tslib_1.__decorate([
    (0, common_1.Get)('whatsapp'),
    tslib_1.__param(0, (0, common_1.Query)('hub.mode')),
    tslib_1.__param(1, (0, common_1.Query)('hub.verify_token')),
    tslib_1.__param(2, (0, common_1.Query)('hub.challenge')),
    tslib_1.__param(3, (0, common_1.Res)()),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [String, String, String, typeof (_e = typeof express_1.Response !== "undefined" && express_1.Response) === "function" ? _e : Object]),
    tslib_1.__metadata("design:returntype", void 0)
], WebhooksController.prototype, "whatsappVerify", null);
tslib_1.__decorate([
    (0, common_1.Post)('whatsapp'),
    (0, common_1.UseGuards)(whatsapp_signature_guard_1.WhatsappSignatureGuard),
    tslib_1.__param(0, (0, common_1.Body)()),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [typeof (_f = typeof Record !== "undefined" && Record) === "function" ? _f : Object]),
    tslib_1.__metadata("design:returntype", Promise)
], WebhooksController.prototype, "whatsappEvents", null);
tslib_1.__decorate([
    (0, common_1.Get)('opt-out'),
    tslib_1.__param(0, (0, common_1.Query)('phone')),
    tslib_1.__param(1, (0, common_1.Query)('email')),
    tslib_1.__param(2, (0, common_1.Query)('channel')),
    tslib_1.__param(3, (0, common_1.Res)()),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [String, String, String, typeof (_g = typeof express_1.Response !== "undefined" && express_1.Response) === "function" ? _g : Object]),
    tslib_1.__metadata("design:returntype", typeof (_h = typeof Promise !== "undefined" && Promise) === "function" ? _h : Object)
], WebhooksController.prototype, "optOutLink", null);
tslib_1.__decorate([
    (0, common_1.Post)('opt-out'),
    tslib_1.__param(0, (0, common_1.Body)()),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [Object]),
    tslib_1.__metadata("design:returntype", Promise)
], WebhooksController.prototype, "optOut", null);
tslib_1.__decorate([
    (0, common_1.Post)('opt-in'),
    tslib_1.__param(0, (0, common_1.Body)()),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [Object]),
    tslib_1.__metadata("design:returntype", Promise)
], WebhooksController.prototype, "optIn", null);
exports.WebhooksController = WebhooksController = WebhooksController_1 = tslib_1.__decorate([
    (0, common_1.Controller)('webhooks'),
    tslib_1.__metadata("design:paramtypes", [typeof (_a = typeof notifications_service_1.NotificationsService !== "undefined" && notifications_service_1.NotificationsService) === "function" ? _a : Object, typeof (_b = typeof customers_service_1.CustomersService !== "undefined" && customers_service_1.CustomersService) === "function" ? _b : Object])
], WebhooksController);
